import { IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import { IoCopy, IoCopyOutline } from "react-icons/io5";
import CustomSnackbar, { CustomSnackbarProps } from "./CustomSnackbar";
import IconWrapper from "./IconWrapper";

type CopyIconButtonProps = {
    defaultText: string;
    copyText: string;
    textToCopy: string;
    snackbarProps?: CustomSnackbarProps;
};

/**
 * CopyIconButton provides a tooltip-wrapped copy-to-clipboard button using either
 * a filled or outlined copy icon. It visually reflects copy success with icon toggle
 * and optional snackbar feedback.
 *
 * Props:
 * - `defaultText` (string) – Tooltip text shown before the copy action.
 * - `copyText` (string) – Tooltip text and snackbar message shown after successful copy.
 * - `textToCopy` (string) – The actual text that will be copied to the clipboard.
 * - `snackbarProps` (CustomSnackbarProps, optional) – Configuration for the CustomSnackbar,
 *   such as position and auto-hide duration.
 *
 * @param {CopyIconButtonProps} props - Props for the CopyIconButton component.
 * @returns {JSX.Element} A tooltip-wrapped copy button with visual and snackbar feedback.
 *
 * @example
 * <CopyIconButton
 *   defaultText="Copy KPI"
 *   copyText="Copied!"
 *   textToCopy="1,204"
 * />
 */
function CopyIconButton({ defaultText, copyText, textToCopy, snackbarProps }: CopyIconButtonProps) {
    const [copyTooltipText, setCopyTooltipText] = useState<string>(defaultText);
    const [snackBaropen, setSnackBaropen] = useState<boolean>(false);
    const [isCopyIconPressed, setIsCopyIconPressed] = useState<boolean>(false);

    const handleClose = () => setSnackBaropen(false);
    const CopyIcon = IconWrapper(IoCopy);
    const CopyIconOutline = IconWrapper(IoCopyOutline);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(textToCopy);
            setIsCopyIconPressed(true);
            setCopyTooltipText(copyText);
            setSnackBaropen(true);

            setTimeout(() => {
                setIsCopyIconPressed(false);
                setCopyTooltipText(defaultText);
            }, 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    return (
        <>
            <Tooltip title={copyTooltipText} arrow>
                <IconButton size="small" onClick={handleCopy}>
                    {!isCopyIconPressed ? (
                        <CopyIconOutline
                            style={{
                                fontSize: 18,
                                color: "action.activate",
                                opacity: isCopyIconPressed ? 0 : 1,
                                transition: "opacity 0.3s ease"
                            }}
                        />
                    ) : (
                        <CopyIcon
                            style={{
                                fontSize: 18,
                                color: "action.activate",
                                opacity: isCopyIconPressed ? 1 : 0,
                                transition: "opacity 0.3s ease"
                            }}
                        />
                    )}
                </IconButton>
            </Tooltip>

            <CustomSnackbar
                key="customSnackbarChatAnswer"
                handleCloseSnackbar={handleClose}
                snackbar={{ open: snackBaropen, message: copyText, severity: "info" }}
                autoHideDuration={2000}
                horizontal={snackbarProps?.horizontal ?? "right"}
                vertical={snackbarProps?.vertical ?? "bottom"}
            />
        </>
    );
}

export default CopyIconButton;
